import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Typography, Grid, Checkbox, FormControlLabel, IconButton, Select, MenuItem, InputBase, useMediaQuery } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import RememberMeCheckboxIcon from '../shared/RememberMeCheckboxIcon';
import { useTheme, type SxProps, type Theme } from '@mui/material/styles';
import NotificationAlert from '../shared/NotificationAlert';
import AuthPanelShell from './AuthPanelShell';
import AuthHeader from './AuthHeader';
import AuthTextField from './AuthTextField';
import PrimaryButton from './PrimaryButton';
import TermsCheckbox from './TermsCheckbox';
import ForgotPasswordModal from './ForgotPasswordModal';
import AsyncButtonContent, { asyncButtonState, useSuccessFlash } from '../motion/AsyncButtonContent';
import authService, { AuthResponse } from '../../services/authService';
import { useDispatch } from 'react-redux';
import { setAuthenticatedUser } from '../../store/authSlice';
import { ensureLoggedOutBeforeNewAuth } from '../../utils/ensureLoggedOutBeforeNewAuth';
import { useLocation, useNavigate } from 'react-router-dom';
export type AuthPanelThemeVariant = 'dark' | 'light';

export const getSignupPhoneValidationError = ({
  clientType,
  smsConsentAccepted,
  areaCode,
  phone,
}: {
  clientType: ClientType;
  smsConsentAccepted: boolean;
  areaCode: string;
  phone: string;
}): string | null => {
  const normalizedAreaCode = areaCode.trim();
  const normalizedPhone = phone.trim();
  const phoneIsRequired = clientType === 'B' || smsConsentAccepted;

  if (!phoneIsRequired && !normalizedPhone) return null;
  if (!normalizedAreaCode) return 'Please enter your area code';
  if (!normalizedPhone) {
    return smsConsentAccepted
      ? 'Please enter your phone number to receive SMS notifications'
      : 'Please enter your phone number';
  }
  return null;
};
export type ClientType = 'C' | 'B';

// B-side role options (English-only)
// 1 - Property Owner (物业业主)
// 2 - Agent (经纪人)
const BSIDE_ROLE_OPTIONS = [{
  value: 1,
  label: 'Property Owner'
}, {
  value: 2,
  label: 'Agent'
}] as const;
type BsideRoleValue = (typeof BSIDE_ROLE_OPTIONS)[number]['value'];
interface AuthPanelProps {
  /**
   * Initial mode when the panel first opens.
   * Defaults to 'signup' to preserve existing TestLoginPage behavior.
   */
  initialMode?: 'login' | 'signup';
  /**
   * Client type for authentication ('C' = Customer, 'B' = Broker/Agent).
   * Defaults to 'C' for backward compatibility.
   */
  clientType?: ClientType;
  /**
   * When true, successful auth navigates to the computed returnTo route.
   * When false, the panel will NOT navigate (useful for modal overlay usage).
   */
  navigateOnAuth?: boolean;
  /**
   * Called after successful authentication (email or Google).
   * The panel itself does not assume whether it should close; the caller decides.
   */
  onAuthenticated?: (authData: AuthResponse) => void;
  /**
   * When true, shows a close (X) button in the top-right of the panel.
   */
  showClose?: boolean;
  /**
   * Called when the user closes the panel via the (X) button.
   * The caller can decide what to do (e.g. reset Smart Search).
   */
  onClose?: () => void;
  /**
   * Visual variant to support light overlay usage (Smart Search) vs dark page usage (Login page).
   */
  themeVariant?: AuthPanelThemeVariant;
  /**
   * AuthPanelShell overrides for reuse contexts.
   */
  shellBackgroundColor?: string;
  shellContainerSx?: SxProps<Theme>;
  width?: string | number;
  matchGoogleButtonToPrimaryOnMobile?: boolean;
}

export const getAuthGoogleButtonSlotMarginTop = (
  mode: 'login' | 'signup',
  isLight: boolean,
  matchPrimaryOnMobile = false
) => {
  const desktopValue = mode === 'login' ? 'auto' : isLight ? '82px' : '57px';
  if (!matchPrimaryOnMobile) return desktopValue;
  return {
    xs: '12px',
    md: desktopValue,
  };
};

export const getAuthGoogleButtonSlotSizing = (
  mode: 'login' | 'signup',
  isLight: boolean,
  matchPrimaryOnMobile = false
) => {
  if (!matchPrimaryOnMobile) {
    return {
      width: '276px',
      maxWidth: '100%',
    };
  }
  return {
    width: { xs: '100%', md: '276px' },
    maxWidth: {
      xs: mode === 'login'
        ? '455px'
        : isLight ? '352px' : '407px',
      md: '276px',
    },
  };
};

export const getAuthGoogleButtonRenderWidth = (
  slotWidth?: number,
  useMeasuredSlot = false
) => {
  if (!useMeasuredSlot || !slotWidth || !Number.isFinite(slotWidth)) return 276;
  return Math.round(slotWidth);
};

export const getAuthGoogleButtonInnerSizingSx = (matchPrimaryOnMobile = false) => {
  if (!matchPrimaryOnMobile) return {};
  return {
    '& > div, & iframe, & .nsm7Bb-HzV7m-LgbsSe': {
      '@media (max-width:899.95px)': {
        width: '100% !important',
        maxWidth: '100% !important',
      },
    },
  };
};

const AuthPanel: React.FC<AuthPanelProps> = ({
  initialMode = 'signup',
  clientType = 'C',
  navigateOnAuth = true,
  onAuthenticated,
  showClose = false,
  onClose,
  themeVariant = 'dark',
  shellBackgroundColor,
  shellContainerSx,
  width,
  matchGoogleButtonToPrimaryOnMobile = false
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobileGoogleButtonLayout = useMediaQuery(theme.breakpoints.down('md'));
  const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
  const isLight = themeVariant === 'light';
  const colors = isLight ? {
    // Figma uses #363434 for the main text in the overlay.
    text: '#363434',
    secondaryText: '#747272',
    strongText: '#4C403E',
    closeIcon: '#363434'
  } : {
    text: 'white',
    secondaryText: 'white',
    strongText: 'white',
    closeIcon: 'white'
  };

  // Figma overlay (Smart Search modal) has slightly different spacing than the full /login page.
  // Keep defaults unchanged (dark variant), and only adjust when themeVariant === 'light'.
  const layout = {
    gridPt: isLight ? '48px' : '45px',
    gridPb: isLight ? '58px' : '58px',
    // Login layout gaps
    loginTitleGap: isLight ? '0px' : '50.259px',
    loginEmailPasswordGap: isLight ? '38px' : '48px',
    loginRowMt: isLight ? '27px' : '10px',
    loginBeforeButtonGap: isLight ? '39px' : '43px',
    // Close icon positioning (modal only; /login doesn't show close)
    closeTop: isLight ? 30 : 18,
    closeRight: isLight ? 30 : 18,
    closeIconSize: isLight ? 26 : 32
  } as const;

  // If a user was redirected here from a ProtectedRoute, send them back after auth.
  const returnTo = (() => {
    const from = (location.state as any)?.from;
    const pathname = from?.pathname;
    const search = from?.search || '';
    const hash = from?.hash || '';
    const candidate = pathname ? `${pathname}${search}${hash}` : null;

    // Default redirect based on client type
    const defaultRedirect = clientType === 'B' ? '/business/dashboard' : '/chat-with-mimi';
    if (!candidate || candidate === '/login' || candidate === '/business/login') {
      return defaultRedirect;
    }
    return candidate;
  })();
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [mode, setMode] = useState<'login' | 'signup'>(initialMode);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [smsConsentAccepted, setSmsConsentAccepted] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);

  // Signup form state
  const [fullName, setFullName] = useState('');
  // B-side signup (new design) uses separate name fields
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [signupEmail, setSignupEmail] = useState('');
  const [signupPassword, setSignupPassword] = useState('');
  const [signupConfirmPassword, setSignupConfirmPassword] = useState('');
  // B-side extra signup fields
  const [businessRole, setBusinessRole] = useState<BsideRoleValue>(2);
  // Phone is optional for C-side and required for B-side; default both to +1.
  const [areaCode, setAreaCode] = useState('1');
  const [phone, setPhone] = useState('');
  const [emailCode, setEmailCode] = useState('');
  const [showEmailCodeField, setShowEmailCodeField] = useState(false);
  const [sendingCode, setSendingCode] = useState(false);
  const [signingUp, setSigningUp] = useState(false);
  const [codeSentFlash, flashCodeSent] = useSuccessFlash();
  // Set when sign-in / sign-up succeeds and kept until the panel leaves (redirect or modal close),
  // so the button goes spinner → check and never falls back to its label during the redirect delay.
  const [loginSucceeded, setLoginSucceeded] = useState(false);
  const [signupSucceeded, setSignupSucceeded] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const hasInlineNotification = Boolean(error || successMessage);
  const consentExtraHeight = 64;
  const optionalPhoneFieldExtraHeight = clientType === 'C' ? 64 : 0;
  const verificationFieldExtraHeight = clientType === 'C' && showEmailCodeField ? 64 : 0;

  // Google OAuth state
  const [googleSdkReady, setGoogleSdkReady] = useState(false);
  const loginGoogleButtonRef = useRef<HTMLDivElement | null>(null);
  const signupGoogleButtonRef = useRef<HTMLDivElement | null>(null);
  const modeRef = useRef<'login' | 'signup'>(mode);
  useEffect(() => {
    modeRef.current = mode;
  }, [mode]);
  const modalShellSx: SxProps<Theme> | undefined = isLight ? {
    // Match Figma overlay panel sizes exactly:
    // - Login:  974×458
    // - Signup: 974×641
    maxWidth: { xs: '100%', md: '974px' },
    width: '100%',
    mx: { xs: 0, md: 0 },
    height: {
      xs: 'auto',
      md: mode === 'login'
        ? '458px'
        : `${641 + consentExtraHeight + optionalPhoneFieldExtraHeight + verificationFieldExtraHeight}px`
    },
    maxHeight: { xs: 'calc(100dvh - 32px)', md: 'none' },
    overflowY: { xs: 'auto', md: 'hidden' },
    transition: 'height 180ms ease',
  } : undefined;
  const bsideShellSx: SxProps<Theme> | undefined = !isLight && clientType === 'B' ? {
    // Match latest Figma for B-side:
    // - Login:  974×458 (kept)
    // - Signup expands to fit separate required terms and optional SMS consent boxes.
    // Extra height for email code field: 40px field + 27px gap = 67px (using consistent ROW_GAP)
    height: {
      xs: 'auto',
      md: mode === 'login' ? '458px' : showEmailCodeField ? `${687 + consentExtraHeight}px` : `${596 + consentExtraHeight}px`
    },
    transition: 'height 180ms ease'
  } : undefined;
  const csideShellSx: SxProps<Theme> | undefined = !isLight && clientType === 'C' ? {
    height: {
      xs: 'auto',
      md: mode === 'login'
        ? '458px'
        : `${640 + optionalPhoneFieldExtraHeight + verificationFieldExtraHeight}px`
    },
    transition: 'height 180ms ease'
  } : undefined;
  const mergedShellSx: SxProps<Theme> | undefined = (() => {
    const parts = [shellContainerSx, modalShellSx, bsideShellSx, csideShellSx].filter(Boolean);
    if (parts.length === 0) return undefined;
    // SxProps supports arrays, but its TS type doesn't model nested SxProps well.
    return parts as any;
  })();
  useEffect(() => {
    if (!googleClientId) {
      console.error('Google client ID is not configured. Set REACT_APP_GOOGLE_CLIENT_ID in your environment.');
      return;
    }
    const scriptId = 'google-identity-service';
    const existingScript = document.getElementById(scriptId) as HTMLScriptElement | null;
    const handleLoad = () => {
      setGoogleSdkReady(true);
    };
    const handleError = () => {
      console.error('Failed to load Google Identity Services script');
    };
    if (existingScript) {
      existingScript.addEventListener('load', handleLoad);
      existingScript.addEventListener('error', handleError);
      if ((window as any).google?.accounts?.id) {
        setGoogleSdkReady(true);
      }
      return () => {
        existingScript.removeEventListener('load', handleLoad);
        existingScript.removeEventListener('error', handleError);
      };
    }
    const script = document.createElement('script');
    script.id = scriptId;
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.addEventListener('load', handleLoad);
    script.addEventListener('error', handleError);
    document.head.appendChild(script);
    return () => {
      script.removeEventListener('load', handleLoad);
      script.removeEventListener('error', handleError);
    };
  }, [googleClientId]);

  const applyAuthSuccess = useCallback((authData: AuthResponse, successCopy: string, delayMs: number) => {
    dispatch(setAuthenticatedUser(authData));
    setError('');
    onAuthenticated?.(authData);
    setSuccessMessage(successCopy);
    if (navigateOnAuth) {
      setTimeout(() => {
        const redirectedFrom = (location.state as any)?.from?.pathname;
        const isAgentSignup = clientType === 'B' && modeRef.current === 'signup';
        const target = isAgentSignup && !redirectedFrom ? '/business/verify' : returnTo;
        navigate(target);
      }, delayMs);
    }
  }, [clientType, dispatch, location.state, navigate, navigateOnAuth, onAuthenticated, returnTo]);
  const finalizeGoogleAuth = useCallback((authData: AuthResponse) => {
    // Mark authentication as completed
    if ((window as any).__googleAuthCompleted) {
      (window as any).__googleAuthCompleted();
      delete (window as any).__googleAuthCompleted;
      delete (window as any).__googleLoginTimeout;
    }
    const currentMode = modeRef.current;
    const successCopy = currentMode === 'signup' ? 'Google sign up successful! Redirecting you to search...' : 'Google login successful! Redirecting you to search...';
    applyAuthSuccess(authData, successCopy, 800);
  }, [applyAuthSuccess]);
  const handleGoogleCredential = useCallback(async (response: any) => {
    const credential = response?.credential;
    if (!credential) {
      setError('Google did not return a credential. Please try again.');
      return;
    }
    try {
      const authData = await authService.verifyGoogleToken(credential, modeRef.current, clientType);
      finalizeGoogleAuth(authData);
    } catch (err: any) {
      setError(err.message || 'Google verification failed. Please try again.');
    }
  }, [clientType, finalizeGoogleAuth]);

  // Initialize Google button - re-run when mode changes
  useEffect(() => {
    if (!googleSdkReady || !googleClientId) {
      return;
    }
    const google = (window as any).google;
    if (!google?.accounts?.id) {
      return;
    }
    google.accounts.id.initialize({
      client_id: googleClientId,
      callback: handleGoogleCredential,
      ux_mode: 'popup',
      cancel_on_tap_outside: true
    });

    // Initialize button for current mode
    const currentRef = mode === 'login' ? loginGoogleButtonRef : signupGoogleButtonRef;
    if (currentRef.current) {
      currentRef.current.innerHTML = '';
      const googleButtonWidth = getAuthGoogleButtonRenderWidth(
        currentRef.current.getBoundingClientRect().width,
        matchGoogleButtonToPrimaryOnMobile && isMobileGoogleButtonLayout
      );
      google.accounts.id.renderButton(currentRef.current, {
        type: 'standard',
        theme: 'outline',
        text: 'continue_with',
        shape: 'pill',
        size: 'large',
        width: googleButtonWidth,
        logo_alignment: 'left'
      });
    }
    return () => {
      if (currentRef.current) {
        currentRef.current.innerHTML = '';
      }
    };
  }, [googleSdkReady, googleClientId, handleGoogleCredential, mode, isMobileGoogleButtonLayout, matchGoogleButtonToPrimaryOnMobile]);
  const resetLoginForm = () => {
    setLoginEmail('');
    setLoginPassword('');
    setRememberMe(false);
    setLoginLoading(false);
  };
  const handleModeSwitch = () => {
    setMode(mode === 'signup' ? 'login' : 'signup');
    // Reset form state when switching modes
    setError('');
    setSuccessMessage('');
    setShowEmailCodeField(false);
    setEmailCode('');
    setBusinessRole(2);
    setAreaCode('1');
    setPhone('');
    setSmsConsentAccepted(false);
    resetLoginForm();
  };
  const isValidEmail = (email: string) => {
    const trimmed = email.trim();
    const isValid = /^\S+@\S+\.\S+$/.test(trimmed);
    return isValid;
  };
  const validatePasswordStrength = (password: string) => {
    const hasUppercase = /[A-Z]/.test(password);
    const hasLowercase = /[a-z]/.test(password);
    const hasNumber = /\d/.test(password);
    const hasMinLength = password.length >= 8;
    return hasUppercase && hasLowercase && hasNumber && hasMinLength;
  };
  const doPasswordsMatch = (password: string, confirmPassword: string) => {
    return password === confirmPassword;
  };

  const handleLogin = async () => {
    if (loginLoading || loginSucceeded) return;
    setError('');
    setSuccessMessage('');
    if (!loginEmail) {
      setError('Please enter your email address');
      return;
    }
    if (!isValidEmail(loginEmail)) {
      setError('Please enter a valid email address');
      return;
    }
    if (!loginPassword) {
      setError('Please enter your password');
      return;
    }
    setLoginLoading(true);
    try {
      const response = await authService.loginWithEmail({
        clientType: clientType,
        email: loginEmail.trim(),
        password: loginPassword,
        enableRememberMe: rememberMe
      });
      setLoginSucceeded(true);
      applyAuthSuccess(response, 'Welcome back! Redirecting you to search...', 800);
    } catch (err: any) {
      setError(err.message || 'Login failed. Please try again.');
    } finally {
      setLoginLoading(false);
    }
  };
  const handleSendCode = async () => {
    if (sendingCode || codeSentFlash) return;
    setError('');
    setSuccessMessage('');
    setEmailCode('');
    const phoneValidationError = getSignupPhoneValidationError({
      clientType,
      smsConsentAccepted,
      areaCode,
      phone,
    });
    if (phoneValidationError) {
      setError(phoneValidationError);
      return;
    }
    if (!signupEmail) {
      setError('Please enter your email address');
      return;
    }
    if (!isValidEmail(signupEmail)) {
      setError('Please enter a valid email address');
      return;
    }
    if (!signupPassword) {
      setError('Please enter a password');
      return;
    }
    if (!signupConfirmPassword) {
      setError('Please confirm your password');
      return;
    }
    if (!doPasswordsMatch(signupPassword, signupConfirmPassword)) {
      setError("Your passwords don't match. Please check and try again.");
      return;
    }
    if (!validatePasswordStrength(signupPassword)) {
      setError('Password must be at least 8 characters and include uppercase, lowercase, and a number.');
      return;
    }
    setSendingCode(true);
    try {
      await ensureLoggedOutBeforeNewAuth();
      const response = await authService.sendEmailCode({
        clientType: clientType,
        email: signupEmail.trim()
      });
      if (response.success) {
        // First send: hold the check on the send button before the code field replaces it.
        if (!showEmailCodeField) await flashCodeSent(600);
        setShowEmailCodeField(true);
        setSuccessMessage('Code sent!');
        setTimeout(() => {
          setSuccessMessage('');
        }, 5000);
      } else {
        setError(response.message || 'Failed to send email code');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to send email code. Please try again.');
    } finally {
      setSendingCode(false);
    }
  };
  const handleSignup = async () => {
    if (signingUp || signupSucceeded) return;
    setError('');
    setSuccessMessage('');
    const phoneValidationError = getSignupPhoneValidationError({
      clientType,
      smsConsentAccepted,
      areaCode,
      phone,
    });
    if (phoneValidationError) {
      setError(phoneValidationError);
      return;
    }
    if (!emailCode) {
      setError('Please enter the verification code');
      return;
    }
    if (emailCode.length !== 6) {
      setError('Verification code must be 6 digits');
      return;
    }
    setSigningUp(true);
    try {
      const nickname = clientType === 'B' ? `${firstName} ${lastName}`.trim() : fullName.trim();
      const response = await authService.registerWithEmailCode({
        clientType: clientType,
        nickname,
        email: signupEmail.trim(),
        password: signupPassword,
        confirmPassword: signupConfirmPassword,
        emailCode: emailCode.trim(),
        ...(clientType === 'B' ? {
          role: businessRole,
          areaCode: areaCode.trim(),
          phone: phone.trim()
        } : phone.trim() ? {
          areaCode: areaCode.trim(),
          phone: phone.trim()
        } : {})
      });
      setSignupSucceeded(true);
      applyAuthSuccess(response, 'Registration successful! You are now logged in.', 1500);
    } catch (err: any) {
      setError(err.message || 'Registration failed. Please try again.');
      setShowEmailCodeField(false);
    } finally {
      setSigningUp(false);
    }
  };
  const handleResendCode = async () => {
    setEmailCode('');
    await handleSendCode();
  };
  const handleCloseInternal = () => {
    setFullName('');
    setFirstName('');
    setLastName('');
    setSignupEmail('');
    setSignupPassword('');
    setSignupConfirmPassword('');
    setBusinessRole(2);
    setAreaCode('1');
    setPhone('');
    setEmailCode('');
    setShowEmailCodeField(false);
    setTermsAccepted(false);
    setSmsConsentAccepted(false);
    setError('');
    setSuccessMessage('');
    setSendingCode(false);
    setSigningUp(false);
    resetLoginForm();
    onClose?.();
  };
  return <>
      <AuthPanelShell mode={mode} onClose={showClose ? handleCloseInternal : undefined} width={width} backgroundColor={shellBackgroundColor} containerSx={mergedShellSx}>
        {showClose && <IconButton onClick={handleCloseInternal} sx={{
        position: 'absolute',
        top: layout.closeTop,
        right: layout.closeRight,
        color: colors.closeIcon,
        zIndex: 20,
        '&:hover': {
          backgroundColor: themeVariant === 'light' ? 'rgba(0,0,0,0.04)' : 'rgba(255,255,255,0.1)'
        }
      }} aria-label="Close">
            <CloseIcon sx={{
          fontSize: layout.closeIconSize
        }} />
          </IconButton>}

        {/* Notification Alert - Top Center Absolute Position */}
        {(error || successMessage) && <Box sx={{
        position: 'absolute',
        top: {
          xs: '20px',
          md: '30px'
        },
        left: {
          xs: 0,
          md: '50%'
        },
        right: {
          xs: 0,
          md: 'auto'
        },
        transform: {
          xs: 'none',
          md: 'translateX(-50%)'
        },
        zIndex: 10,
        width: {
          xs: '100%',
          md: 'fit-content'
        },
        px: {
          xs: 3,
          md: 0
        },
        display: 'flex',
        justifyContent: 'center',
        boxSizing: 'border-box'
      }}>
            {error ? <NotificationAlert type="error" message={error} onClose={() => setError('')} sx={{
          whiteSpace: 'nowrap'
        }} /> : <NotificationAlert type="success" message={successMessage} onClose={() => setSuccessMessage('')} sx={{
          whiteSpace: 'nowrap'
        }} />}
          </Box>}

        {/** B-side signup has an updated fixed-position layout per Figma */}
        {(() => {
        const isBsideSignupV2 = !isLight && clientType === 'B' && mode === 'signup';
        return <Grid container sx={{
          height: '100%',
          pt: isBsideSignupV2 ? {
            xs: hasInlineNotification ? '72px' : 0,
            md: 0
          } : {
            xs: hasInlineNotification ? `calc(${layout.gridPt} + 72px)` : layout.gridPt,
            md: layout.gridPt
          },
          pb: isBsideSignupV2 ? 0 : layout.gridPb
        }} spacing={0}>
          {mode === 'login' ? <>
              {/* LOGIN layout */}
              <Grid item xs={12} md={7} order={{
              xs: 1,
              md: 1
            }} sx={{
              height: {
                xs: 'auto',
                md: '100%'
              }
            }}>
                <Box component="form" noValidate onSubmit={e => {
                e.preventDefault();
                handleLogin();
              }} sx={{
                width: {
                  xs: '100%',
                  md: '455px'
                },
                display: 'flex',
                flexDirection: 'column',
                pl: {
                  xs: 3,
                  md: '68px'
                },
                pr: {
                  xs: 3,
                  md: 0
                },
                height: {
                  xs: 'auto',
                  md: '100%'
                },
                boxSizing: {
                  xs: 'border-box',
                  md: 'content-box'
                }
              }}>
                  <Box sx={{
                  width: '239px',
                  height: {
                    xs: 'auto',
                    md: '111px'
                  },
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  mb: {
                    xs: 2,
                    md: 0
                  }
                }}>
                    <AuthHeader mode={mode} color={colors.text} />
                  </Box>

                  <Box sx={{
                  height: {
                    xs: '20px',
                    md: layout.loginTitleGap
                  }
                }} />

                  <Box sx={{
                  height: '40px',
                  display: 'flex',
                  alignItems: 'center'
                }}>
                    <AuthTextField placeholder="Email address" type="email" value={loginEmail} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLoginEmail(e.target.value)} disabled={loginLoading} />
                  </Box>

                  <Box sx={{
                  height: {
                    xs: '20px',
                    md: layout.loginEmailPasswordGap
                  }
                }} />

                  <Box sx={{
                  height: '40px',
                  display: 'flex',
                  alignItems: 'center'
                }}>
                    <AuthTextField placeholder="Enter your password" type="password" value={loginPassword} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLoginPassword(e.target.value)} disabled={loginLoading} />
                  </Box>

                  <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mt: layout.loginRowMt,
                  width: '100%',
                  maxWidth: '455px',
                  flexWrap: 'nowrap',
                  gap: {
                    xs: 1.5,
                    md: 0
                  }
                }}>
                    <FormControlLabel control={<Checkbox size="small" icon={<RememberMeCheckboxIcon checkColor={isLight ? '#FFFAF7' : '#4C403E'} />} checkedIcon={<RememberMeCheckboxIcon checked checkColor={isLight ? '#FFFAF7' : '#4C403E'} />} checked={rememberMe} onChange={e => setRememberMe(e.target.checked)} sx={{
                    color: colors.text,
                    '&.Mui-checked': {
                      color: colors.text
                    }
                  }} />} label="Remember me" sx={{
                    color: colors.text,
                    m: 0,
                    '& .MuiFormControlLabel-label': {
                      fontFamily: 'Montserrat, sans-serif',
                      fontSize: {
                        xs: "var(--type-small)",
                        md: "var(--type-body)"
                      },
                      lineHeight: 'normal',
                      whiteSpace: 'nowrap'
                    }
                  }} />
                    <Typography onClick={() => setShowForgotPassword(true)} sx={{
                    fontFamily: 'Montserrat, sans-serif',
                    fontSize: {
                      xs: "var(--type-small)",
                      md: "var(--type-body)"
                    },
                    fontWeight: 'bold',
                    color: colors.text,
                    cursor: 'pointer',
                    whiteSpace: 'nowrap'
                  }}>
                      Forgot password?
                    </Typography>
                  </Box>

                  <Box sx={{
                  height: {
                    xs: '20px',
                    md: layout.loginBeforeButtonGap
                  }
                }} />

                  <Box sx={{
                  height: '40px',
                  display: 'flex',
                  alignItems: 'center'
                }}>
                    <PrimaryButton type="submit" sx={{
                    width: '100%',
                    height: '40px',
                    maxWidth: '455px'
                  }} aria-busy={loginLoading || loginSucceeded}>
                      <AsyncButtonContent state={asyncButtonState(loginLoading, loginSucceeded)} iconSize={24} loadingLabel="Logging in">Log In</AsyncButtonContent>
                    </PrimaryButton>
                  </Box>
                </Box>
              </Grid>

              {/* RIGHT COLUMN */}
              <Grid item xs={12} md={5} order={{
              xs: 12,
              md: 2
            }}>
                <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: {
                  xs: 'center',
                  md: 'flex-start'
                },
                pt: {
                  xs: 3,
                  md: '200px'
                },
                pb: {
                  xs: 3,
                  md: 0
                },
                pl: {
                  xs: 3,
                  md: isLight ? '56px' : '101px'
                },
                pr: {
                  xs: 3,
                  md: 0
                },
                width: '100%',
                height: {
                  xs: 'auto',
                  md: '100%'
                }
              }}>
                  <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: {
                    xs: 'center',
                    md: 'flex-start'
                  },
                  width: '100%',
                  maxWidth: {
                    md: '264px'
                  },
                  height: '17px',
                  gap: {
                    xs: '10px',
                    md: 0
                  }
                }}>
                    <Typography sx={{
                    fontFamily: 'Montserrat, sans-serif',
                    fontSize: "var(--type-small)",
                    fontWeight: 400,
                    color: colors.text,
                    textAlign: 'center',
                    width: {
                      xs: 'auto',
                      md: '179px'
                    },
                    lineHeight: 'normal',
                    whiteSpace: 'nowrap'
                  }}>
                      Don't have an account?
                    </Typography>
                    <Typography onClick={handleModeSwitch} sx={{
                    fontFamily: 'Montserrat, sans-serif',
                    fontSize: "var(--type-small)",
                    fontWeight: 700,
                    color: colors.text,
                    cursor: 'pointer',
                    textDecoration: 'underline',
                    ml: {
                      xs: 0,
                      md: '25px'
                    },
                    width: {
                      xs: 'auto',
                      md: '60px'
                    },
                    textAlign: 'center',
                    lineHeight: 'normal',
                    whiteSpace: 'nowrap'
                  }}>
                      Sign up
                    </Typography>
                  </Box>

                  <Box sx={{
                  width: '23px',
                  height: '25px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  alignSelf: 'center',
                  mt: {
                    xs: '10px',
                    md: '15px'
                  }
                }}>
                    <Typography sx={{
                    fontFamily: '"Open Sans", sans-serif',
                    fontSize: "var(--type-h3)",
                    fontWeight: 700,
                    color: colors.strongText,
                    textAlign: 'center',
                    lineHeight: 'normal'
                  }}>
                      Or
                    </Typography>
                  </Box>

                  <Box sx={{
                  ...getAuthGoogleButtonSlotSizing('login', isLight, matchGoogleButtonToPrimaryOnMobile),
                  height: '40px',
                  display: 'flex',
                  alignItems: 'flex-start',
                  mt: getAuthGoogleButtonSlotMarginTop('login', isLight, matchGoogleButtonToPrimaryOnMobile),
                  alignSelf: 'flex-start',
                  position: 'relative'
                }}>
                    <Box ref={loginGoogleButtonRef} sx={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    ...getAuthGoogleButtonInnerSizingSx(matchGoogleButtonToPrimaryOnMobile),
                    '& > div': {
                      borderRadius: '20px !important'
                    }
                  }} />
                  </Box>
                </Box>
              </Grid>
            </> : <>
              {/* SIGNUP layout */}
              {/** B-side signup: match Figma panel layout exactly on desktop */}
              {!isLight && clientType === 'B' ? <Grid item xs={12} sx={{
              height: {
                xs: 'auto',
                md: '100%'
              },
              position: 'relative'
            }}>
                  {(() => {
                const EMAIL_CODE_EXTRA_HEIGHT = 91;
                // Desktop coordinates below are from Figma node 7947:2240 (974x596 base panel).
                const TITLE_LEFT = 68;
                const TITLE_TOP = 59;
                const FIELDS_LEFT = 65;
                const FIELDS_TOP = 168;
                const TERMS_LEFT = 65;
                const TERMS_TOP_BASE = 487;
                const SWITCH_ROW_LEFT = 333;
                const SWITCH_ROW_TOP_BASE = 535;
                const CONTENT_W = 843;
                const FIELD_W = 400;
                const ROW_GAP = 27;
                const COL_GAP = 43;
                const TERMS_TOP = showEmailCodeField ? TERMS_TOP_BASE + EMAIL_CODE_EXTRA_HEIGHT : TERMS_TOP_BASE;
                const SWITCH_ROW_TOP = (showEmailCodeField ? SWITCH_ROW_TOP_BASE + EMAIL_CODE_EXTRA_HEIGHT : SWITCH_ROW_TOP_BASE) + consentExtraHeight;
                return <>
                        {/* Desktop: absolute-positioned layout (hidden on mobile) */}
                        <Box sx={{
                    display: {
                      xs: 'none',
                      md: 'block'
                    },
                    height: '100%',
                    position: 'relative'
                  }}>
                          <Box sx={{
                      position: 'absolute',
                      left: `${TITLE_LEFT}px`,
                      top: `${TITLE_TOP}px`,
                      width: '239px',
                      height: '111px',
                      display: 'flex',
                      alignItems: 'center'
                    }}>
                            <AuthHeader mode={mode} color={colors.text} />
                          </Box>

                          <Box component="form" noValidate onSubmit={e => {
                      e.preventDefault();
                      if (showEmailCodeField) {
                        handleSignup();
                      } else {
                        handleSendCode();
                      }
                    }} sx={{
                      position: 'absolute',
                      left: `${FIELDS_LEFT}px`,
                      top: `${FIELDS_TOP}px`,
                      width: `${CONTENT_W}px`,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: `${ROW_GAP}px`
                    }}>
                            <Box sx={{
                        height: '40px',
                        display: 'flex',
                        alignItems: 'center'
                      }}>
                              <AuthTextField placeholder="Role" type="text" value={businessRole === 2 ? 'Agent' : 'Property Owner'} onChange={() => {}} disabled inputWidth={CONTENT_W} />
                            </Box>
                            <Box sx={{
                        display: 'flex',
                        gap: `${COL_GAP}px`
                      }}>
                              <Box sx={{
                          width: `${FIELD_W}px`,
                          height: '40px',
                          display: 'flex',
                          alignItems: 'center'
                        }}>
                                <AuthTextField placeholder="First Name" value={firstName} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFirstName(e.target.value)} disabled={showEmailCodeField} inputWidth={FIELD_W} />
                              </Box>
                              <Box sx={{
                          width: `${FIELD_W}px`,
                          height: '40px',
                          display: 'flex',
                          alignItems: 'center'
                        }}>
                                <AuthTextField placeholder="Last Name" value={lastName} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLastName(e.target.value)} disabled={showEmailCodeField} inputWidth={FIELD_W} />
                              </Box>
                            </Box>
                            <Box sx={{
                        display: 'flex',
                        gap: `${COL_GAP}px`
                      }}>
                              <Box sx={{
                          width: `${FIELD_W}px`,
                          height: '40px',
                          display: 'flex',
                          alignItems: 'center'
                        }}>
                                <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            backgroundColor: 'white',
                            borderRadius: '20px',
                            height: '40px',
                            width: `${FIELD_W}px`,
                            overflow: 'hidden',
                            opacity: showEmailCodeField ? 0.6 : 1,
                            pointerEvents: showEmailCodeField ? 'none' : 'auto'
                          }}>
                                  <Select value={areaCode} onChange={e => setAreaCode(e.target.value as string)} variant="standard" disableUnderline MenuProps={{
                              anchorOrigin: {
                                vertical: 'bottom',
                                horizontal: 'left'
                              },
                              transformOrigin: {
                                vertical: 'top',
                                horizontal: 'left'
                              },
                              PaperProps: {
                                sx: {
                                  mt: '6px',
                                  borderRadius: '12px'
                                }
                              }
                            }} sx={{
                              fontFamily: 'Montserrat, sans-serif',
                              fontSize: "var(--type-body)",
                              color: '#000',
                              pl: '14px',
                              minWidth: '78px',
                              '& .MuiSelect-select': {
                                pr: '20px !important',
                                pl: '0 !important',
                                py: '0 !important'
                              },
                              '& .MuiSelect-icon': {
                                right: '2px',
                                top: 'calc(50% - 12px)'
                              }
                            }}>
                                    <MenuItem value="1">+1</MenuItem>
                                    <MenuItem value="86">+86</MenuItem>
                                  </Select>
                                  <Box sx={{
                              width: '1px',
                              height: '22px',
                              bgcolor: 'rgba(0,0,0,0.15)',
                              flexShrink: 0
                            }} />
                                  <InputBase placeholder="Phone number" type="tel" value={phone} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPhone(e.target.value.replace(/\D/g, ''))} sx={{
                              flex: 1,
                              fontFamily: 'Montserrat, sans-serif',
                              fontSize: "var(--type-h3)",
                              color: '#000',
                              pl: '14px',
                              pr: '16px',
                              '& input::placeholder': {
                                color: 'rgba(0, 0, 0, 0.5)',
                                opacity: 1
                              }
                            }} />
                                </Box>
                              </Box>
                              <Box sx={{
                          width: `${FIELD_W}px`,
                          height: '40px',
                          display: 'flex',
                          alignItems: 'center'
                        }}>
                                <AuthTextField placeholder="Email address" type="email" value={signupEmail} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSignupEmail(e.target.value)} disabled={showEmailCodeField} inputWidth={FIELD_W} />
                              </Box>
                            </Box>
                            <Box sx={{
                        display: 'flex',
                        gap: `${COL_GAP}px`
                      }}>
                              <Box sx={{
                          width: `${FIELD_W}px`,
                          height: '40px',
                          display: 'flex',
                          alignItems: 'center'
                        }}>
                                <AuthTextField placeholder="Enter your password" type="password" value={signupPassword} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSignupPassword(e.target.value)} disabled={showEmailCodeField} inputWidth={FIELD_W} />
                              </Box>
                              <Box sx={{
                          width: `${FIELD_W}px`,
                          height: '40px',
                          display: 'flex',
                          alignItems: 'center'
                        }}>
                                <AuthTextField placeholder="Confirm your password" type="password" value={signupConfirmPassword} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSignupConfirmPassword(e.target.value)} disabled={showEmailCodeField} inputWidth={FIELD_W} />
                              </Box>
                            </Box>
                            {!showEmailCodeField ? <Box sx={{
                        height: '40px',
                        display: 'flex',
                        alignItems: 'center'
                      }}>
                                <PrimaryButton sx={{
                          width: `${CONTENT_W}px`,
                          minWidth: `${CONTENT_W}px`,
                          maxWidth: `${CONTENT_W}px`,
                          height: '40px',
                          '&:disabled': {
                            backgroundColor: '#9d8985',
                            color: 'rgba(255, 255, 255, 0.7)',
                            boxShadow: 'none'
                          }
                        }} onClick={handleSendCode} disabled={!termsAccepted} aria-busy={sendingCode || codeSentFlash}>
                                  <AsyncButtonContent state={asyncButtonState(sendingCode, codeSentFlash)} iconSize={24} loadingLabel="Sending code">Send email code</AsyncButtonContent>
                                </PrimaryButton>
                              </Box> : <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: `${ROW_GAP}px`
                      }}>
                                <Box sx={{
                          height: '40px',
                          display: 'flex',
                          alignItems: 'center'
                        }}>
                                  <AuthTextField placeholder="Enter 6-Digit Code" type="text" value={emailCode} onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                            setEmailCode(value);
                          }} inputProps={{
                            maxLength: 6
                          }} inputWidth={CONTENT_W} />
                                </Box>
                                <Box sx={{
                          height: '40px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: `${COL_GAP}px`
                        }}>
                                  <PrimaryButton sx={{
                            width: `${Math.floor((CONTENT_W - COL_GAP) / 2)}px`,
                            height: '40px',
                            whiteSpace: 'nowrap',
                            px: '10px',
                            backgroundColor: '#9d8985',
                            color: 'white',
                            '&:hover': {
                              backgroundColor: '#8b7a77'
                            },
                            '&:disabled': {
                              backgroundColor: '#b5a9a6',
                              color: 'rgba(255, 255, 255, 0.7)'
                            }
                          }} onClick={handleResendCode} disabled={sendingCode}>Resend Code</PrimaryButton>
                                  <PrimaryButton sx={{
                            width: `${Math.floor((CONTENT_W - COL_GAP) / 2)}px`,
                            height: '40px',
                            whiteSpace: 'nowrap',
                            px: '35px'
                          }} onClick={handleSignup} aria-busy={signingUp || signupSucceeded}>
                                    <AsyncButtonContent state={asyncButtonState(signingUp, signupSucceeded)} iconSize={24} loadingLabel="Signing up">Complete Sign Up</AsyncButtonContent>
                                  </PrimaryButton>
                                </Box>
                              </Box>}
                          </Box>

                          <Box sx={{
                      position: 'absolute',
                      left: `${TERMS_LEFT}px`,
                      top: `${TERMS_TOP}px`,
                      width: `${CONTENT_W}px`
                    }}>
                            <TermsCheckbox checked={termsAccepted} onChange={setTermsAccepted} smsChecked={smsConsentAccepted} onSmsChange={setSmsConsentAccepted} disabled={showEmailCodeField} color={colors.text} linkColor={colors.text} disabledColor="rgba(255,255,255,0.5)" />
                          </Box>

                          <Box sx={{
                      position: 'absolute',
                      left: `${SWITCH_ROW_LEFT}px`,
                      top: `${SWITCH_ROW_TOP}px`,
                      display: 'flex',
                      alignItems: 'center',
                      height: '17px'
                    }}>
                            <Typography sx={{
                        fontFamily: 'Montserrat, sans-serif',
                        fontSize: "var(--type-small)",
                        fontWeight: 400,
                        color: colors.text,
                        textAlign: 'center',
                        width: '179px',
                        lineHeight: 'normal',
                        whiteSpace: 'nowrap'
                      }}>Already have an account?</Typography>
                            <Typography onClick={handleModeSwitch} sx={{
                        fontFamily: 'Montserrat, sans-serif',
                        fontSize: "var(--type-small)",
                        fontWeight: 700,
                        color: colors.text,
                        cursor: 'pointer',
                        textDecoration: 'underline',
                        ml: '25px',
                        width: '60px',
                        textAlign: 'center',
                        lineHeight: 'normal'
                      }}>Log in</Typography>
                          </Box>
                        </Box>

                        {/* Mobile: stacked flow layout (hidden on desktop) */}
                        <Box component="form" noValidate onSubmit={e => {
                    e.preventDefault();
                    if (showEmailCodeField) {
                      handleSignup();
                    } else {
                      handleSendCode();
                    }
                  }} sx={{
                    display: {
                      xs: 'flex',
                      md: 'none'
                    },
                    flexDirection: 'column',
                    gap: 2,
                    px: 3,
                    py: 3
                  }}>
                          <AuthHeader mode={mode} color={colors.text} />
                          <AuthTextField placeholder="Role" type="text" value={businessRole === 2 ? 'Agent' : 'Property Owner'} onChange={() => {}} disabled />
                          <AuthTextField placeholder="First Name" value={firstName} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFirstName(e.target.value)} disabled={showEmailCodeField} />
                          <AuthTextField placeholder="Last Name" value={lastName} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLastName(e.target.value)} disabled={showEmailCodeField} />
                          <Box sx={{
                      display: 'flex',
                      alignItems: 'center',
                      backgroundColor: 'white',
                      borderRadius: '20px',
                      height: '40px',
                      width: '100%',
                      maxWidth: '455px',
                      overflow: 'hidden',
                      opacity: showEmailCodeField ? 0.6 : 1,
                      pointerEvents: showEmailCodeField ? 'none' : 'auto'
                    }}>
                            <Select value={areaCode} onChange={e => setAreaCode(e.target.value as string)} variant="standard" disableUnderline MenuProps={{
                        anchorOrigin: {
                          vertical: 'bottom',
                          horizontal: 'left'
                        },
                        transformOrigin: {
                          vertical: 'top',
                          horizontal: 'left'
                        },
                        PaperProps: {
                          sx: {
                            mt: '6px',
                            borderRadius: '12px'
                          }
                        }
                      }} sx={{
                        fontFamily: 'Montserrat, sans-serif',
                        fontSize: "var(--type-body)",
                        color: '#000',
                        pl: '14px',
                        minWidth: '78px',
                        '& .MuiSelect-select': {
                          pr: '20px !important',
                          pl: '0 !important',
                          py: '0 !important'
                        },
                        '& .MuiSelect-icon': {
                          right: '2px',
                          top: 'calc(50% - 12px)'
                        }
                      }}>
                              <MenuItem value="1">+1</MenuItem>
                              <MenuItem value="86">+86</MenuItem>
                            </Select>
                            <Box sx={{
                        width: '1px',
                        height: '22px',
                        bgcolor: 'rgba(0,0,0,0.15)',
                        flexShrink: 0
                      }} />
                            <InputBase placeholder="Phone number" type="tel" value={phone} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPhone(e.target.value.replace(/\D/g, ''))} sx={{
                        flex: 1,
                        fontFamily: 'Montserrat, sans-serif',
                        fontSize: "var(--type-body)",
                        color: '#000',
                        pl: '14px',
                        pr: '16px',
                        '& input::placeholder': {
                          color: 'rgba(0, 0, 0, 0.5)',
                          opacity: 1
                        }
                      }} />
                          </Box>
                          <AuthTextField placeholder="Email address" type="email" value={signupEmail} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSignupEmail(e.target.value)} disabled={showEmailCodeField} />
                          <AuthTextField placeholder="Enter your password" type="password" value={signupPassword} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSignupPassword(e.target.value)} disabled={showEmailCodeField} />
                          <AuthTextField placeholder="Confirm your password" type="password" value={signupConfirmPassword} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSignupConfirmPassword(e.target.value)} disabled={showEmailCodeField} />
                          {showEmailCodeField && <AuthTextField placeholder="Enter 6-Digit Code" type="text" value={emailCode} onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                      setEmailCode(value);
                    }} inputProps={{
                      maxLength: 6
                    }} />}
                          {!showEmailCodeField ? <PrimaryButton sx={{
                      width: '100%',
                      maxWidth: '455px',
                      height: '40px',
                      '&:disabled': {
                        backgroundColor: '#9d8985',
                        color: 'rgba(255, 255, 255, 0.7)',
                        boxShadow: 'none'
                      }
                    }} onClick={handleSendCode} disabled={!termsAccepted} aria-busy={sendingCode || codeSentFlash}>
                              <AsyncButtonContent state={asyncButtonState(sendingCode, codeSentFlash)} iconSize={24} loadingLabel="Sending code">Send email code</AsyncButtonContent>
                            </PrimaryButton> : <Box sx={{
                      display: 'flex',
                      gap: 1,
                      width: '100%',
                      maxWidth: '455px'
                    }}>
                              <PrimaryButton sx={{
                        flex: 1,
                        height: '40px',
                        whiteSpace: 'nowrap',
                        px: '10px',
                        backgroundColor: '#9d8985',
                        color: 'white',
                        '&:hover': {
                          backgroundColor: '#8b7a77'
                        },
                        '&:disabled': {
                          backgroundColor: '#b5a9a6',
                          color: 'rgba(255, 255, 255, 0.7)'
                        }
                      }} onClick={handleResendCode} disabled={sendingCode}>Resend Code</PrimaryButton>
                              <PrimaryButton sx={{
                        flex: 1,
                        height: '40px',
                        whiteSpace: 'nowrap',
                        px: '10px'
                      }} onClick={handleSignup} aria-busy={signingUp || signupSucceeded}>
                                <AsyncButtonContent state={asyncButtonState(signingUp, signupSucceeded)} iconSize={24} loadingLabel="Signing up">Complete Sign Up</AsyncButtonContent>
                              </PrimaryButton>
                            </Box>}
                          <TermsCheckbox checked={termsAccepted} onChange={setTermsAccepted} smsChecked={smsConsentAccepted} onSmsChange={setSmsConsentAccepted} disabled={showEmailCodeField} color={colors.text} linkColor={colors.text} disabledColor="rgba(255,255,255,0.5)" />
                          <Box sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mt: 1
                    }}>
                            <Typography sx={{
                        fontFamily: 'Montserrat, sans-serif',
                        fontSize: "var(--type-small)",
                        fontWeight: 400,
                        color: colors.text,
                        lineHeight: 'normal'
                      }}>Already have an account?</Typography>
                            <Typography onClick={handleModeSwitch} sx={{
                        fontFamily: 'Montserrat, sans-serif',
                        fontSize: "var(--type-small)",
                        fontWeight: 700,
                        color: colors.text,
                        cursor: 'pointer',
                        textDecoration: 'underline',
                        ml: '10px',
                        lineHeight: 'normal'
                      }}>Log in</Typography>
                          </Box>
                        </Box>
                      </>;
              })()}
                </Grid> : <Grid item xs={12} md={7} order={{
              xs: 1,
              md: 1
            }}>
                {/** Figma signup overlay uses 400px wide inputs/buttons. Keep /login page unchanged (455px). */}
                {(() => {
                const signupFieldWidth = isLight ? 400 : 455;
                return <Box component="form" noValidate onSubmit={e => {
                  e.preventDefault();
                  if (showEmailCodeField) {
                    handleSignup();
                  } else {
                    handleSendCode();
                  }
                }} sx={{
                  width: '100%',
                  maxWidth: `${signupFieldWidth}px`,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '24px',
                  justifyContent: 'space-between',
                  position: 'relative',
                  pl: {
                    xs: 3,
                    md: '68px'
                  },
                  pr: {
                    xs: 3,
                    md: 0
                  },
                  boxSizing: {
                    xs: 'border-box',
                    md: 'content-box'
                  }
                }}>
                  <Box sx={{
                    width: '239px',
                    height: '111px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center'
                  }}>
                    <AuthHeader mode={mode} color={colors.text} />
                  </Box>

                  <Box sx={{
                    height: '40px',
                    display: 'flex',
                    alignItems: 'center'
                  }}>
                    <AuthTextField placeholder="Full name" value={fullName} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFullName(e.target.value)} disabled={showEmailCodeField} inputWidth={signupFieldWidth} />
                  </Box>

                  <Box sx={{
                    height: '40px',
                    display: 'flex',
                    alignItems: 'center'
                  }}>
                    <AuthTextField placeholder="Email address" type="email" value={signupEmail} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSignupEmail(e.target.value)} disabled={showEmailCodeField} inputWidth={signupFieldWidth} />
                  </Box>

                  <Box sx={{
                    height: '40px',
                    display: 'flex',
                    alignItems: 'center'
                  }}>
                    <AuthTextField placeholder="Enter your password" type="password" value={signupPassword} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSignupPassword(e.target.value)} disabled={showEmailCodeField} inputWidth={signupFieldWidth} />
                  </Box>

                  <Box sx={{
                    height: '40px',
                    display: 'flex',
                    alignItems: 'center'
                  }}>
                    <AuthTextField placeholder="Confirm password" type="password" value={signupConfirmPassword} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSignupConfirmPassword(e.target.value)} disabled={showEmailCodeField} inputWidth={signupFieldWidth} />
                  </Box>

                  {clientType === 'B' &&
                      <Box sx={{
                      height: '40px',
                      display: 'flex',
                      alignItems: 'center'
                    }}>
                        <AuthTextField placeholder="Business Type/Role" type="text" value="Agent" onChange={() => {}} disabled={true} inputWidth={signupFieldWidth} />
                      </Box>}

                  <Box sx={{
                      height: '40px',
                      display: 'flex',
                      alignItems: 'center'
                    }}>
                        {/* Seamless area-code dropdown + phone input */}
                        <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        backgroundColor: 'white',
                        borderRadius: '20px',
                        height: '40px',
                        width: `${signupFieldWidth}px`,
                        overflow: 'hidden',
                        opacity: showEmailCodeField ? 0.6 : 1,
                        pointerEvents: showEmailCodeField ? 'none' : 'auto'
                      }}>
                          <Select value={areaCode} onChange={e => setAreaCode(e.target.value as string)} variant="standard" disableUnderline MenuProps={{
                          anchorOrigin: {
                            vertical: 'bottom',
                            horizontal: 'left'
                          },
                          transformOrigin: {
                            vertical: 'top',
                            horizontal: 'left'
                          },
                          PaperProps: {
                            sx: {
                              mt: '6px',
                              borderRadius: '12px'
                            }
                          }
                        }} sx={{
                          fontFamily: 'Montserrat, sans-serif',
                          fontSize: "var(--type-body)",
                          color: '#000',
                          pl: '14px',
                          minWidth: '78px',
                          '& .MuiSelect-select': {
                            pr: '20px !important',
                            pl: '0 !important',
                            py: '0 !important'
                          },
                          '& .MuiSelect-icon': {
                            right: '2px',
                            top: 'calc(50% - 12px)'
                          }
                        }}>
                            <MenuItem value="1">+1</MenuItem>
                            <MenuItem value="86">+86</MenuItem>
                          </Select>
                          <Box sx={{
                          width: '1px',
                          height: '22px',
                          bgcolor: 'rgba(0,0,0,0.15)',
                          flexShrink: 0
                        }} />
                          <InputBase
                            placeholder={clientType === 'B'
                              ? 'Phone number'
                              : smsConsentAccepted
                              ? 'Phone number (required for SMS)'
                              : 'Phone number (optional)'}
                            required={clientType === 'B' || smsConsentAccepted}
                            type="tel"
                            value={phone}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPhone(e.target.value.replace(/\D/g, ''))}
                            sx={{
                          flex: 1,
                          fontFamily: 'Montserrat, sans-serif',
                          fontSize: "var(--type-h3)",
                          color: '#000',
                          pl: '14px',
                          pr: '16px',
                          '& input::placeholder': {
                            color: 'rgba(0, 0, 0, 0.5)',
                            opacity: 1
                          }
                        }} />
                        </Box>
                      </Box>

                  {showEmailCodeField && <Box sx={{
                    height: '40px',
                    display: 'flex',
                    alignItems: 'center'
                  }}>
                      <AuthTextField placeholder="Enter 6-digit code" type="text" value={emailCode} onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                      setEmailCode(value);
                    }} inputProps={{
                      maxLength: 6
                    }} inputWidth={signupFieldWidth} />
                    </Box>}

                  {!showEmailCodeField ? <Box sx={{
                    height: '40px',
                    display: 'flex',
                    alignItems: 'center'
                  }}>
                      <PrimaryButton sx={{
                      width: '100%',
                      maxWidth: `${signupFieldWidth}px`,
                      height: '40px',
                      '&:disabled': {
                        backgroundColor: '#9d8985',
                        color: 'rgba(255, 255, 255, 0.7)',
                        boxShadow: 'none'
                      }
                    }} onClick={handleSendCode} disabled={!termsAccepted} aria-busy={sendingCode || codeSentFlash}>
                        <AsyncButtonContent state={asyncButtonState(sendingCode, codeSentFlash)} iconSize={24} loadingLabel="Sending code">Send me email code</AsyncButtonContent>
                      </PrimaryButton>
                    </Box> : <Box sx={{
                    height: '40px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                  }}>
                      <PrimaryButton sx={{
                      flex: 1,
                      height: '40px',
                      whiteSpace: 'nowrap',
                      px: '10px',
                      backgroundColor: '#9d8985',
                      color: 'white',
                      '&:hover': {
                        backgroundColor: '#8b7a77'
                      },
                      '&:disabled': {
                        backgroundColor: '#b5a9a6',
                        color: 'rgba(255, 255, 255, 0.7)'
                      }
                    }} onClick={handleResendCode} disabled={sendingCode}>
                        Resend Code
                      </PrimaryButton>
                      <PrimaryButton sx={{
                      flex: 1,
                      height: '40px',
                      whiteSpace: 'nowrap',
                      px: {
                        xs: '10px',
                        md: '35px'
                      }
                    }} onClick={handleSignup} aria-busy={signingUp || signupSucceeded}>
                        <AsyncButtonContent state={asyncButtonState(signingUp, signupSucceeded)} iconSize={24} loadingLabel="Signing up">Complete Sign Up</AsyncButtonContent>
                      </PrimaryButton>
                    </Box>}

                  <Box>
                    <TermsCheckbox checked={termsAccepted} onChange={setTermsAccepted} smsChecked={smsConsentAccepted} onSmsChange={setSmsConsentAccepted} disabled={showEmailCodeField} color={colors.text} linkColor={colors.text} disabledColor={themeVariant === 'light' ? 'rgba(76, 64, 62, 0.45)' : 'rgba(255,255,255,0.5)'} />
                  </Box>
                </Box>;
              })()}
              </Grid>}

              {/* Right column (Google + switch mode). Not used in the new B-side signup panel. */}
              {!(!isLight && clientType === 'B') && <Grid item xs={12} md={5} order={{
              xs: 12,
              md: 2
            }}>
                  <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: {
                  xs: 'center',
                  md: 'flex-start'
                },
                pt: {
                  xs: 3,
                  md: isLight ? '263px' : '280px'
                },
                pb: {
                  xs: 3,
                  md: 0
                },
                pl: {
                  xs: 3,
                  md: isLight ? '56px' : '101px'
                },
                pr: {
                  xs: 3,
                  md: 0
                },
                width: '100%',
                height: {
                  xs: 'auto',
                  md: '100%'
                }
              }}>
                    <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  height: '17px'
                }}>
                      <Typography sx={{
                    fontFamily: 'Montserrat, sans-serif',
                    fontSize: "var(--type-small)",
                    fontWeight: 400,
                    color: colors.text,
                    textAlign: 'center',
                    width: '179px',
                    lineHeight: 'normal',
                    whiteSpace: 'nowrap'
                  }}>
                        Already have an account?
                      </Typography>
                      <Typography onClick={handleModeSwitch} sx={{
                    fontFamily: 'Montserrat, sans-serif',
                    fontSize: "var(--type-small)",
                    fontWeight: 700,
                    color: colors.text,
                    cursor: 'pointer',
                    textDecoration: 'underline',
                    ml: '25px',
                    width: '60px',
                    textAlign: 'center',
                    lineHeight: 'normal'
                  }}>
                        Log in
                      </Typography>
                    </Box>

                    <Box sx={{
                  width: '23px',
                  height: '25px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  alignSelf: 'center',
                  mt: isLight ? '3px' : '15px'
                }}>
                      <Typography sx={{
                    fontFamily: '"Open Sans", sans-serif',
                    fontSize: "var(--type-h3)",
                    fontWeight: 700,
                    color: colors.strongText,
                    textAlign: 'center',
                    lineHeight: 'normal'
                  }}>
                        Or
                      </Typography>
                    </Box>

                    <Box sx={{
                  ...getAuthGoogleButtonSlotSizing('signup', isLight, matchGoogleButtonToPrimaryOnMobile),
                  height: '40px',
                  display: 'flex',
                  alignItems: 'flex-start',
                  mt: getAuthGoogleButtonSlotMarginTop('signup', isLight, matchGoogleButtonToPrimaryOnMobile),
                  alignSelf: 'flex-start',
                  position: 'relative'
                }}>
                      <Box ref={signupGoogleButtonRef} sx={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    ...getAuthGoogleButtonInnerSizingSx(matchGoogleButtonToPrimaryOnMobile),
                    '& > div': {
                      borderRadius: '20px !important'
                    }
                  }} />
                    </Box>
                  </Box>
                </Grid>}
            </>}
        </Grid>;
      })()}
      </AuthPanelShell>

      <ForgotPasswordModal open={showForgotPassword} onClose={() => setShowForgotPassword(false)} clientType={clientType} />
    </>;
};
export default AuthPanel;
